import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import {
  Search, Database, FileSearch, ShieldAlert, UserSearch, ExternalLink,
  Clock, User, FolderOpen, Plus, Trash2, Tag, Save, RefreshCw, Star,
  Phone, Mail, MapPin, Car, Users, FileText, AlertTriangle, CheckCircle2,
  Download,
} from 'lucide-react';

// Tool icons mapping
const TOOL_ICONS = {
  database: Database,
  'file-search': FileSearch,
  search: Search,
  'user-search': UserSearch,
  'shield-alert': ShieldAlert
};

// Note type colors
const NOTE_TYPE_COLORS = {
  address: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  phone: 'bg-green-500/20 text-green-400 border-green-500/30',
  vehicle: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  associate: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  employer: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  social: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  'public-record': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  caution: 'bg-red-500/20 text-red-400 border-red-500/30',
  verified: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  unverified: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  general: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
};

const NOTE_TYPES = [
  { value: 'address', label: 'Address' },
  { value: 'phone', label: 'Phone' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'associate', label: 'Associate' },
  { value: 'employer', label: 'Employer' },
  { value: 'social', label: 'Social Media' },
  { value: 'public-record', label: 'Public Record' },
  { value: 'caution', label: 'Caution' },
  { value: 'verified', label: 'Verified' },
  { value: 'unverified', label: 'Unverified' },
  { value: 'general', label: 'General' }
];

const SkipTraceTools = () => {
  const [searchParams] = useSearchParams();
  const linkedCaseId = searchParams.get('caseId');
  const token = localStorage.getItem('nexus_token');
  
  const [tools, setTools] = useState([]);
  const [activity, setActivity] = useState([]);
  const [notes, setNotes] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tools');
  
  // Search prep form
  const [searchPrep, setSearchPrep] = useState({
    case_id: linkedCaseId || '',
    subject_name: '',
    alias: '',
    dob: '',
    phone: '',
    email: '',
    last_known_address: '',
    license_plate: '',
    vehicle: '',
    relative_associate: '',
    notes: ''
  });
  
  // New note form
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState({
    case_id: linkedCaseId || '',
    note_type: 'general',
    content: '',
    source: ''
  });

  // Iter144 — tool category filter + favorite pinning (per-user, localStorage)
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem('nx_skiptrace_favs') || '[]'); } catch { return []; }
  });
  const toggleFavorite = (toolId) => {
    setFavorites(prev => {
      const next = prev.includes(toolId) ? prev.filter(x => x !== toolId) : [...prev, toolId];
      localStorage.setItem('nx_skiptrace_favs', JSON.stringify(next));
      return next;
    });
  };

  // Iter145 — saved search-prep history for PDF export
  const [prepHistory, setPrepHistory] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [toolsRes, activityRes, notesRes, casesRes, prepRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/skip-trace/tools`, { headers }),
        fetch(`${BACKEND_URL}/api/skip-trace/activity${linkedCaseId ? `?case_id=${linkedCaseId}` : ''}`, { headers }),
        fetch(`${BACKEND_URL}/api/skip-trace/notes${linkedCaseId ? `?case_id=${linkedCaseId}` : ''}`, { headers }),
        fetch(`${BACKEND_URL}/api/cases`, { headers }),
        fetch(`${BACKEND_URL}/api/skip-trace/search-prep${linkedCaseId ? `?case_id=${linkedCaseId}` : ''}`, { headers }),
      ]);
      
      if (toolsRes.ok) setTools(await toolsRes.json());
      if (activityRes.ok) setActivity(await activityRes.json());
      if (notesRes.ok) setNotes(await notesRes.json());
      if (casesRes.ok) setCases(await casesRes.json());
      if (prepRes.ok) setPrepHistory(await prepRes.json());
    } catch (e) {
      console.error('Fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [token, linkedCaseId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update form when linked case changes
  useEffect(() => {
    if (linkedCaseId) {
      setSearchPrep(prev => ({ ...prev, case_id: linkedCaseId }));
      setNewNote(prev => ({ ...prev, case_id: linkedCaseId }));
    }
  }, [linkedCaseId]);

  const handleLaunchTool = async (tool, logToCase = true) => {
    // Master Directive Phase 8/9 — require an active case & enforce rate limits.
    if (!searchPrep.case_id) {
      toast({
        title: 'Active case required',
        description: 'Select a linked case before launching skiptrace tools.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/skip-trace/log-launch?tool_id=${tool.id}&case_id=${searchPrep.case_id}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.status === 429) {
        const body = await res.json().catch(() => ({}));
        const detail = body?.detail || {};
        const exp = detail.lockout_expires_at ? new Date(detail.lockout_expires_at).toLocaleTimeString() : '';
        toast({
          title: 'Rate limit reached',
          description: `${detail.message || 'Skip-trace access paused.'}${exp ? ` Retry after ${exp}.` : ''}`,
          variant: 'destructive',
        });
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast({ title: 'Launch blocked', description: body?.detail || 'Unable to launch tool', variant: 'destructive' });
        return;
      }
      const data = await res.json().catch(() => ({}));
      window.open(tool.url, '_blank');
      toast({
        title: 'Tool Launched',
        description: `${tool.name} opened. Usage ${data.daily_usage || '?'}/${data.daily_limit || 20} today.`,
      });
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleSaveSearchPrep = async ({ exportPdf = false } = {}) => {
    if (!searchPrep.subject_name && !searchPrep.phone && !searchPrep.last_known_address) {
      toast({ title: 'Error', description: 'Please enter at least one search criteria', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/skip-trace/search-prep`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(searchPrep),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast({ title: 'Save failed', description: data?.detail || `HTTP ${res.status}`, variant: 'destructive' });
        return null;
      }
      const prepId = data.prep_id;
      // Keep local history in sync so the UI confirms the save.
      setPrepHistory(prev => [{ ...(data.prep || {}), id: prepId }, ...prev].slice(0, 50));
      toast({ title: 'Saved', description: `Search prep saved as ${prepId}` });
      if (exportPdf && prepId) await downloadSearchPrepPdf(prepId);
      return prepId;
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
      return null;
    }
  };

  const downloadSearchPrepPdf = async (prepId) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/skip-trace/search-prep/${prepId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        toast({ title: 'PDF export failed', description: d?.detail || `HTTP ${r.status}`, variant: 'destructive' });
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `search-prep-${prepId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ title: 'PDF exported', description: `Downloaded search-prep-${prepId}.pdf` });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleAddNote = async () => {
    if (!newNote.content.trim()) {
      toast({ title: 'Error', description: 'Note content is required', variant: 'destructive' });
      return;
    }
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/skip-trace/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(newNote)
      });
      
      if (res.ok) {
        toast({ title: 'Success', description: 'Research note added' });
        setShowAddNote(false);
        setNewNote({ case_id: linkedCaseId || '', note_type: 'general', content: '', source: '' });
        fetchData();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await fetch(`${BACKEND_URL}/api/skip-trace/notes/${noteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Deleted', description: 'Note deleted' });
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const linkedCase = cases.find(c => c.id === searchPrep.case_id);

  return (
    <div className="space-y-6" data-testid="skip-trace-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 flex items-center justify-center" style={{ background: 'var(--nx-blue)', borderRadius: 10, boxShadow: '0 6px 14px -4px rgba(59,130,246,0.55)' }}>
            <Search className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="nx-eyebrow">Field Research</div>
            <h1 className="nx-h1 mt-1">Skip Trace Tools</h1>
            <p className="nx-body mt-1">Investigation research workspace and tool launcher</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {linkedCase && (
            <Badge className="bg-[#1D4ED8]/20 text-[#3B82F6] border border-[#1D4ED8]/30 px-3 py-1.5">
              <FolderOpen className="w-4 h-4 mr-2" />
              Linked: {linkedCase.case_number}
            </Badge>
          )}
          <Button variant="outline" className="border-[#334155] text-[#94A3B8] hover:bg-[#182136]" onClick={fetchData}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#131A2A] border border-[#334155] p-1">
          <TabsTrigger value="tools" className="data-[state=active]:bg-[#1D4ED8] data-[state=active]:text-white text-[#94A3B8] px-4">
            <Search className="w-4 h-4 mr-2" /> Search Tools
          </TabsTrigger>
          <TabsTrigger value="prep" className="data-[state=active]:bg-[#1D4ED8] data-[state=active]:text-white text-[#94A3B8] px-4">
            <FileText className="w-4 h-4 mr-2" /> Search Prep
          </TabsTrigger>
          <TabsTrigger value="notes" className="data-[state=active]:bg-[#1D4ED8] data-[state=active]:text-white text-[#94A3B8] px-4">
            <Tag className="w-4 h-4 mr-2" /> Research Notes
            {notes.length > 0 && <Badge className="ml-2 bg-[#182136] text-[#94A3B8] text-xs">{notes.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="activity" className="data-[state=active]:bg-[#1D4ED8] data-[state=active]:text-white text-[#94A3B8] px-4">
            <Clock className="w-4 h-4 mr-2" /> Activity Log
            {activity.length > 0 && <Badge className="ml-2 bg-[#182136] text-[#94A3B8] text-xs">{activity.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* Search Tools Tab */}
        <TabsContent value="tools" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Tool Cards */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-[#F8FAFC]">Quick Launch Tools</h3>
                <Badge className="bg-amber-500/10 text-amber-300 border border-amber-500/30 text-[10px]" data-testid="rate-limit-badge">
                  Limit 20 / tool / day · 2h lockout
                </Badge>
              </div>
              {/* Iter144 — category filter chips */}
              <div className="flex flex-wrap gap-1.5 mb-3" data-testid="skiptrace-category-chips">
                {['ALL', ...Array.from(new Set(tools.map(t => t.type)))].map(cat => (
                  <button
                    key={cat}
                    onClick={() => setTypeFilter(cat)}
                    data-testid={`chip-${cat.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                      typeFilter === cat
                        ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]'
                        : 'bg-[#131A2A] text-[#94A3B8] border-[#334155] hover:border-[#3B82F6]/40'
                    }`}
                  >
                    {cat}
                    {cat !== 'ALL' && (
                      <span className="ml-1.5 opacity-70">{tools.filter(t => t.type === cat).length}</span>
                    )}
                  </button>
                ))}
              </div>
              {!searchPrep.case_id && (
                <div className="mb-3 flex items-start gap-2 rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2" data-testid="case-required-warning">
                  <AlertTriangle className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-100 leading-snug">
                    <strong>Active case required.</strong> Link a case below before launching any skiptrace tool — every launch is audited.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {tools
                  .filter(t => typeFilter === 'ALL' || t.type === typeFilter)
                  .slice()
                  .sort((a, b) => {
                    const af = favorites.includes(a.id) ? 0 : 1;
                    const bf = favorites.includes(b.id) ? 0 : 1;
                    return af - bf;
                  })
                  .map(tool => {
                  const Icon = TOOL_ICONS[tool.icon] || Search;
                  const isFav = favorites.includes(tool.id);
                  return (
                    <Card key={tool.id} className={`bg-[#131A2A] border-[#334155] hover:border-[#1D4ED8]/50 transition-colors ${isFav ? 'ring-1 ring-amber-500/40' : ''}`} data-testid={`tool-card-${tool.id}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-[#1D4ED8]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Icon className="w-6 h-6 text-[#3B82F6]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <h4 className="text-[#F8FAFC] font-semibold">{tool.name}</h4>
                                <Badge className="bg-[#182136] text-[#94A3B8] text-xs mt-1 border border-[#334155]">{tool.type}</Badge>
                              </div>
                              <button
                                onClick={() => toggleFavorite(tool.id)}
                                className={`p-1 rounded hover:bg-[#182136] transition-colors ${isFav ? 'text-amber-400' : 'text-[#64748B]'}`}
                                title={isFav ? 'Unpin from top' : 'Pin to top'}
                                data-testid={`pin-${tool.id}`}
                              >
                                <Star className="w-4 h-4" fill={isFav ? 'currentColor' : 'none'} />
                              </button>
                            </div>
                            <p className="text-sm text-[#94A3B8] mt-2">{tool.description}</p>
                            {tool.investigator_warning && (
                              <div className="mt-2 flex items-start gap-1.5 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1.5" data-testid={`investigator-warning-${tool.id}`}>
                                <AlertTriangle className="w-3.5 h-3.5 text-amber-300 flex-shrink-0 mt-0.5" />
                                <p className="text-[11px] text-amber-200 leading-snug"><strong className="text-amber-100">NOTE:</strong> {tool.investigator_warning}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[#334155]">
                          <Button 
                            onClick={() => handleLaunchTool(tool, true)}
                            className="flex-1 bg-[#1D4ED8] hover:bg-[#1E40AF] text-white"
                            data-testid={`launch-tool-${tool.id}`}
                          >
                            <ExternalLink className="w-4 h-4 mr-2" /> Launch & Log
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => handleLaunchTool(tool, false)}
                            className="border-[#334155] text-[#94A3B8] hover:bg-[#182136]"
                            title="Launch without logging"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Quick Reference Panel */}
            <div className="space-y-4">
              <Card className="bg-[#131A2A] border-[#334155]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-[#F8FAFC] text-base">Link to Case</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Select value={searchPrep.case_id || 'none'} onValueChange={v => setSearchPrep({...searchPrep, case_id: v === 'none' ? '' : v})}>
                    <SelectTrigger className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]">
                      <SelectValue placeholder="Select case (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#131A2A] border-[#334155]">
                      <SelectItem value="none">None</SelectItem>
                      {cases.map(c => (
                        <SelectItem key={c.id} value={c.id} className="text-[#F8FAFC]">
                          {c.case_number} - {c.title?.substring(0, 30)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {linkedCase && (
                    <div className="p-3 bg-[#182136] rounded-lg border border-[#334155]">
                      <p className="text-sm text-[#F8FAFC] font-medium">{linkedCase.title}</p>
                      <p className="text-xs text-[#94A3B8] mt-1">{linkedCase.case_type?.replace(/_/g, ' ')}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-[#131A2A] border-[#334155]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-[#F8FAFC] text-base">Quick Subject Info</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Input 
                    value={searchPrep.subject_name}
                    onChange={e => setSearchPrep({...searchPrep, subject_name: e.target.value})}
                    className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                    placeholder="Subject Name"
                  />
                  <Input 
                    value={searchPrep.phone}
                    onChange={e => setSearchPrep({...searchPrep, phone: e.target.value})}
                    className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                    placeholder="Phone Number"
                  />
                  <Input 
                    value={searchPrep.last_known_address}
                    onChange={e => setSearchPrep({...searchPrep, last_known_address: e.target.value})}
                    className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                    placeholder="Last Known Address"
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Search Prep Tab */}
        <TabsContent value="prep" className="mt-6">
          <Card className="bg-[#131A2A] border-[#334155]">
            <CardHeader>
              <CardTitle className="text-[#F8FAFC] flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#3B82F6]" />
                Search Preparation / Lead Intake
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Case Link */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#94A3B8]">Link to Case</Label>
                  <Select value={searchPrep.case_id || 'none'} onValueChange={v => setSearchPrep({...searchPrep, case_id: v === 'none' ? '' : v})}>
                    <SelectTrigger className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]">
                      <SelectValue placeholder="Select case" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#131A2A] border-[#334155]">
                      <SelectItem value="none">None</SelectItem>
                      {cases.map(c => (
                        <SelectItem key={c.id} value={c.id} className="text-[#F8FAFC]">
                          {c.case_number} - {c.title?.substring(0, 40)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Subject Info */}
              <div className="space-y-4">
                <h4 className="text-[#F8FAFC] font-medium flex items-center gap-2">
                  <User className="w-4 h-4 text-[#3B82F6]" /> Subject Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-[#94A3B8]">Subject Name</Label>
                    <Input 
                      value={searchPrep.subject_name}
                      onChange={e => setSearchPrep({...searchPrep, subject_name: e.target.value})}
                      className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8]">Alias / Nickname</Label>
                    <Input 
                      value={searchPrep.alias}
                      onChange={e => setSearchPrep({...searchPrep, alias: e.target.value})}
                      className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8]">Date of Birth</Label>
                    <Input 
                      type="date"
                      value={searchPrep.dob}
                      onChange={e => setSearchPrep({...searchPrep, dob: e.target.value})}
                      className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-4">
                <h4 className="text-[#F8FAFC] font-medium flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#3B82F6]" /> Contact Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#94A3B8]">Phone Number</Label>
                    <Input 
                      value={searchPrep.phone}
                      onChange={e => setSearchPrep({...searchPrep, phone: e.target.value})}
                      className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8]">Email Address</Label>
                    <Input 
                      value={searchPrep.email}
                      onChange={e => setSearchPrep({...searchPrep, email: e.target.value})}
                      className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                    />
                  </div>
                </div>
              </div>

              {/* Location Info */}
              <div className="space-y-4">
                <h4 className="text-[#F8FAFC] font-medium flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#3B82F6]" /> Location Information
                </h4>
                <div>
                  <Label className="text-[#94A3B8]">Last Known Address</Label>
                  <Input 
                    value={searchPrep.last_known_address}
                    onChange={e => setSearchPrep({...searchPrep, last_known_address: e.target.value})}
                    className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                  />
                </div>
              </div>

              {/* Vehicle Info */}
              <div className="space-y-4">
                <h4 className="text-[#F8FAFC] font-medium flex items-center gap-2">
                  <Car className="w-4 h-4 text-[#3B82F6]" /> Vehicle Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#94A3B8]">License Plate</Label>
                    <Input 
                      value={searchPrep.license_plate}
                      onChange={e => setSearchPrep({...searchPrep, license_plate: e.target.value})}
                      className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8]">Vehicle Description</Label>
                    <Input 
                      value={searchPrep.vehicle}
                      onChange={e => setSearchPrep({...searchPrep, vehicle: e.target.value})}
                      className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                      placeholder="Make, model, color"
                    />
                  </div>
                </div>
              </div>

              {/* Associates */}
              <div className="space-y-4">
                <h4 className="text-[#F8FAFC] font-medium flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#3B82F6]" /> Known Associates
                </h4>
                <div>
                  <Label className="text-[#94A3B8]">Relative / Associate</Label>
                  <Textarea 
                    value={searchPrep.relative_associate}
                    onChange={e => setSearchPrep({...searchPrep, relative_associate: e.target.value})}
                    className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                    rows={2}
                    placeholder="Names, relationships, contact info"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-[#94A3B8]">Additional Notes</Label>
                <Textarea 
                  value={searchPrep.notes}
                  onChange={e => setSearchPrep({...searchPrep, notes: e.target.value})}
                  className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                  rows={3}
                />
              </div>

              {/* Save Button */}
              <div className="flex justify-end gap-2 pt-4 border-t border-[#334155]">
                <Button onClick={() => handleSaveSearchPrep()} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white"
                        data-testid="prep-save-btn">
                  <Save className="w-4 h-4 mr-2" /> Save Search Prep
                </Button>
                <Button onClick={() => handleSaveSearchPrep({ exportPdf: true })}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="prep-save-pdf-btn">
                  <Download className="w-4 h-4 mr-2" /> Save &amp; Download PDF
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Iter145 — Saved search-prep history with per-row PDF export */}
          <Card className="bg-[#131A2A] border-[#334155] mt-4" data-testid="prep-history-card">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-[#F8FAFC] text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#3B82F6]" /> Saved Search Preps
                <Badge className="bg-[#182136] text-[#94A3B8] border border-[#334155] text-xs ml-1">{prepHistory.length}</Badge>
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={fetchData} className="text-[#94A3B8] hover:text-white">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {prepHistory.length === 0 ? (
                <div className="p-5 text-center text-sm text-[#94A3B8]">
                  No saved search preps yet. Save one above to build an auditable history.
                </div>
              ) : (
                <div className="divide-y divide-[#334155]">
                  {prepHistory.slice(0, 20).map(p => (
                    <div key={p.id} className="flex items-center justify-between px-4 py-3 hover:bg-[#0B0F19]/60"
                         data-testid={`prep-row-${p.id}`}>
                      <div className="min-w-0">
                        <div className="text-[#F8FAFC] font-medium truncate">
                          {p.subject_name || p.phone || p.last_known_address || '— no name —'}
                        </div>
                        <div className="text-[11px] text-[#94A3B8] font-mono mt-0.5">
                          {p.id} · {p.created_at ? new Date(p.created_at).toLocaleString() : '—'}
                          {p.case_id ? ` · ${p.case_id}` : ''}
                        </div>
                      </div>
                      <Button size="sm" variant="outline"
                              onClick={() => downloadSearchPrepPdf(p.id)}
                              className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                              data-testid={`prep-pdf-${p.id}`}>
                        <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Research Notes Tab */}
        <TabsContent value="notes" className="mt-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#F8FAFC]">Research Notes</h3>
              <Dialog open={showAddNote} onOpenChange={setShowAddNote}>
                <DialogTrigger asChild>
                  <Button className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" data-testid="add-research-note-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Note
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#131A2A] border-[#334155] text-[#F8FAFC]">
                  <DialogHeader>
                    <DialogTitle>Add Research Note</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label className="text-[#94A3B8]">Link to Case</Label>
                      <Select value={newNote.case_id || 'none'} onValueChange={v => setNewNote({...newNote, case_id: v === 'none' ? '' : v})}>
                        <SelectTrigger className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]">
                          <SelectValue placeholder="Select case" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#131A2A] border-[#334155]">
                          <SelectItem value="none">None</SelectItem>
                          {cases.map(c => (
                            <SelectItem key={c.id} value={c.id} className="text-[#F8FAFC]">{c.case_number}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#94A3B8]">Note Type</Label>
                      <Select value={newNote.note_type} onValueChange={v => setNewNote({...newNote, note_type: v})}>
                        <SelectTrigger className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#131A2A] border-[#334155]">
                          {NOTE_TYPES.map(t => (
                            <SelectItem key={t.value} value={t.value} className="text-[#F8FAFC]">{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#94A3B8]">Source</Label>
                      <Input 
                        value={newNote.source}
                        onChange={e => setNewNote({...newNote, source: e.target.value})}
                        className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                        placeholder="TLOxp, PublicData, etc."
                      />
                    </div>
                    <div>
                      <Label className="text-[#94A3B8]">Note Content *</Label>
                      <Textarea 
                        value={newNote.content}
                        onChange={e => setNewNote({...newNote, content: e.target.value})}
                        className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC]"
                        rows={4}
                        placeholder="Enter your research findings..."
                      />
                    </div>
                    <Button onClick={handleAddNote} className="w-full bg-[#1D4ED8] hover:bg-[#1E40AF] text-white">
                      Save Note
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {notes.length === 0 ? (
              <Card className="bg-[#131A2A] border-[#334155]">
                <CardContent className="p-12 text-center">
                  <Tag className="w-16 h-16 mx-auto text-[#475569] mb-4" />
                  <h3 className="text-xl font-semibold text-[#F8FAFC] mb-2">No Research Notes</h3>
                  <p className="text-[#94A3B8]">Add notes to track your skip trace research findings</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {notes.map(note => (
                  <Card key={note.id} className="bg-[#131A2A] border-[#334155]" data-testid={`note-${note.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`${NOTE_TYPE_COLORS[note.note_type] || NOTE_TYPE_COLORS.general} text-xs border`}>
                            {note.note_type?.replace(/-/g, ' ')}
                          </Badge>
                          {note.source && (
                            <Badge className="bg-[#182136] text-[#94A3B8] text-xs border border-[#334155]">
                              Source: {note.source}
                            </Badge>
                          )}
                          {note.case_id && (
                            <Badge className="bg-[#1D4ED8]/20 text-[#3B82F6] text-xs border border-[#1D4ED8]/30">
                              <FolderOpen className="w-3 h-3 mr-1" />
                              {cases.find(c => c.id === note.case_id)?.case_number || note.case_id}
                            </Badge>
                          )}
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-[#94A3B8] hover:text-[#DC2626] hover:bg-[#182136]"
                          onClick={() => handleDeleteNote(note.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-[#F8FAFC] text-sm whitespace-pre-wrap">{note.content}</p>
                      <div className="flex items-center gap-3 mt-3 pt-3 border-t border-[#334155] text-xs text-[#94A3B8]">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" /> {note.created_by_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {new Date(note.created_at).toLocaleString()}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Activity Log Tab */}
        <TabsContent value="activity" className="mt-6">
          <Card className="bg-[#131A2A] border-[#334155]">
            <CardHeader>
              <CardTitle className="text-[#F8FAFC] flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#3B82F6]" />
                Search Activity Log
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activity.length === 0 ? (
                <div className="p-8 text-center">
                  <Clock className="w-12 h-12 mx-auto text-[#475569] mb-2" />
                  <p className="text-[#94A3B8]">No activity logged yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activity.map(log => (
                    <div key={log.id} className="flex items-center gap-4 p-3 bg-[#0B0F19] rounded-lg border border-[#334155]" data-testid={`activity-${log.id}`}>
                      <div className="w-10 h-10 bg-[#1D4ED8]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <ExternalLink className="w-5 h-5 text-[#3B82F6]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[#F8FAFC] font-medium">{log.tool_name}</p>
                        <p className="text-xs text-[#94A3B8]">
                          Launched by {log.user_name}
                          {log.case_id && (
                            <span className="ml-2">
                              • Case: {cases.find(c => c.id === log.case_id)?.case_number || log.case_id}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="text-xs text-[#94A3B8] flex-shrink-0">
                        {new Date(log.launched_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SkipTraceTools;
